import {validatePhoneNumber} from './form-utils'

export * from '@salesforce/retail-react-app/app/utils/address-utils'

/**
 * Extracts valid OrderAddress fields from an address object
 * @param {Object} address - The address object (may contain extra fields from customer address)
 * @returns {Object} Clean address object with only OrderAddress fields
 */
export const cleanAddressForOrder = (address) => {
    if (!address) return null

    return {
        address1: address.address1,
        address2: address.address2 || '',
        city: address.city,
        countryCode: address.countryCode,
        firstName: address.firstName,
        lastName: address.lastName,
        phone: address.phone,
        postalCode: address.postalCode,
        stateCode: address.stateCode,
        c_shippingAddressType: address.addressType
    }
}

/**
 * Checks whether an address has a first and last name that satisfy the checkout
 * minimum length requirement (at least 2 characters). Bolt allows single-character
 * names, which the SFCC checkout form rejects, so this guards against treating such
 * addresses as complete.
 * @param {Object} address - The address object to validate
 * @returns {boolean} True when both first and last name are at least 2 characters
 */
export const isAddressNameValid = (address) => {
    return (
        (address?.firstName?.trim().length ?? 0) > 1 && (address?.lastName?.trim().length ?? 0) > 1
    )
}

/**
 * Normalizes a Bolt phone only when it is clearly a US number (+1 / leading 1).
 * Non-US international numbers are left intact so they are not sliced into 10
 * digits that can coincidentally pass NANP validation
 * (e.g. "+919876543210" → "9876543210").
 * @param {string} phoneNumber - Raw phone from Bolt
 * @returns {string|undefined} Normalized phone string
 */
export const normalizeBoltPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return phoneNumber

    const original = String(phoneNumber).trim()
    const digits = original.replace(/\D/g, '')

    // US with country code: +1XXXXXXXXXX or 1XXXXXXXXXX
    if (digits.length === 11 && digits.startsWith('1')) {
        return digits.slice(1)
    }

    // Non-US international — keep original so validation can reject it before
    // any last-10 truncation turns it into a false NANP match.
    if (original.startsWith('+') || digits.length > 10) {
        return original
    }

    return digits.length === 10 ? digits : original
}

/**
 * Checks whether an address phone is a valid US/NANP number. Bolt may return
 * international numbers; those should be treated as incomplete so the phone
 * field can be highlighted and corrected.
 * Empty phone is allowed (phone remains optional on the address form).
 * Uses the same rules as the checkout field validator, including optional extensions
 * (e.g. "(512) 555-1234 x123"), so saved addresses with extensions are not treated
 * as incomplete.
 * @param {Object} address - The address object to validate
 * @returns {boolean} True when phone is empty or a valid US number
 */
export const isAddressPhoneValid = (address) => {
    const phone = address?.phone?.trim()
    if (!phone) return true

    const countryCode = address?.countryCode?.toUpperCase?.()
    if (countryCode && countryCode !== 'US' && countryCode !== 'USA') {
        return false
    }

    // Explicit international prefix that is not US (+1...)
    if (phone.startsWith('+')) {
        const digits = phone.replace(/\s*x\d+\s*$/i, '').replace(/\D/g, '')
        if (digits.length === 11 && digits.startsWith('1')) {
            return validatePhoneNumber(digits.slice(1))
        }
        return false
    }

    // Match the form field validator (supports formatting + optional "x" extension).
    if (validatePhoneNumber(phone)) return true

    // Also accept bare 11-digit US numbers with a leading country code "1".
    // Strip a trailing extension so it is not counted toward the digit length.
    const withoutExtension = phone.replace(/\s*x\d+\s*$/i, '')
    const digits = withoutExtension.replace(/\D/g, '')
    if (digits.length === 11 && digits.startsWith('1')) {
        return validatePhoneNumber(digits.slice(1))
    }
    return false
}

export const cleanBoltAddressForOrder = (boltAddress) => {
    return {
        firstName: boltAddress?.first_name,
        lastName: boltAddress?.last_name,
        address1: boltAddress?.street_address1,
        address2: boltAddress?.street_address2,
        city: boltAddress?.locality,
        postalCode: boltAddress?.postal_code,
        stateCode: boltAddress?.region_code,
        countryCode: boltAddress?.country_code,
        phone: normalizeBoltPhoneNumber(boltAddress?.phone_number),
        c_boltAddressId: boltAddress?.id
    }
}
